import {
	AlertBar,
	Checkbox,
	Form,
	FormItem,
	ToolbarAction,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import {
	disable,
	enableAndSave,
	EnvironmentExchangeSettingsSavingModel,
	EnvironmentExchangeSettingsViewModel,
	getPageViewModel
} from "client/Api/Email/ExchangeSettings/environmentExchangeSettings";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { useSidebarMenuStore } from "../SidebarMenu/sidebarMenuStore";
import { FetchCallback } from "client/zustand/createAutoFetchStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const ExchangeSettings = (): ReactElement => {
	const [isEnabled, setIsEnabled] = useState<boolean>(false);
	const [originalIsEnabled, setOriginalIsEnabled] = useState<boolean>(false);
	const [formData, setFormData] = useState<EnvironmentExchangeSettingsSavingModel>(DEFAULT_SAVE_MODEL);
	const [originalFormData, setOriginalFormData] = useState<EnvironmentExchangeSettingsSavingModel>(DEFAULT_SAVE_MODEL);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [wasAlertDismissed, setWasAlertDismissed] = useState<boolean>(false);

	const formHandler: IFormHandler<EnvironmentExchangeSettingsSavingModel> =
		useFormHandler<EnvironmentExchangeSettingsSavingModel>(
		formData,
		setFormData,
		"NoValidation"
	);

	const alertManager = useAlerts();
	const globalCancellationToken: AbortSignal = useUnmountedCancellation();
	useActiveSettingsMenuItem(SettingsPageKind.Exchange);
	const initializeSidebar: FetchCallback = useSidebarMenuStore(x => x.fetch);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const model: EnvironmentExchangeSettingsViewModel = await getPageViewModel(signal)
						.catch(handlePageDataLoadingError);

					const formData: EnvironmentExchangeSettingsSavingModel = convertToSaveModel(model);

					setIsEnabled(model.isEnabled);
					setOriginalIsEnabled(model.isEnabled);
					setFormData(formData);
					setOriginalFormData(formData);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const saveHandler = useCallback(
		async(): Promise<void> => {
			if(isBusy) {
				return;
			}
			setIsBusy(true);
			try {
				if(isEnabled) {
					await enableAndSave(formData, globalCancellationToken);
				} else {
					await disable(globalCancellationToken);
				}
				setOriginalFormData(formData);
				setOriginalIsEnabled(isEnabled);
				setIsBusy(false);
				alertManager.showSuccess(localize("_ChangesSaved"));
				initializeSidebar({ forceReload: true });
			} catch(e) {
				if(isCancelledError(e)) {
					return;
				}
				setIsBusy(false);
				const isValidationError = formHandler
					.validation
					.tryProcessServerError(e);

				if(isValidationError){
					return;
				}
				console.error(e);
				alertManager.showError(localize("_ChangesNotSaved"));
			}
		},
		[
			isBusy,
			isEnabled,
			formData,
			formHandler.validation,
			alertManager,
			globalCancellationToken,
			initializeSidebar
		]
	);

	const onCancel = useCallback(
		(): void => {
			setFormData(originalFormData);
			setIsEnabled(originalIsEnabled);
			setWasAlertDismissed(false);
		},
		[originalFormData, originalIsEnabled]
	);

	const defaultActions: ToolbarAction<undefined>[] = useMemo(
		() => listDefaultActions({
			onSave: saveHandler,
			onCancel
		}),
		[saveHandler, onCancel]
	);

	const topBarActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => {
			return buildActionsForTopBar<undefined, ToolbarAction<undefined>>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={undefined}
			title={localize("_ExchangeSettings")}
		>
			<Form onSubmit="NONE" hasWidthConstrained >
				<FormItem label={localize("_EnableExchange")}>
					<Checkbox
						text={localize("_EnableExchange")}
						value={isEnabled}
						onChange={() => setIsEnabled(!isEnabled)}
					/>
				</FormItem>
				{
					isEnabled && (
						<>
							<FormItem label={localize("_ActivityFeed")}>
								<Checkbox
									{...formHandler.for("areNewEmailActivitiesEnabled")
										.attachToCheckbox()
										.withOnChange(
											value => {
												if (value) {
													setWasAlertDismissed(false);
												}
											}
										)}
									text={localize("_EnableEmailActivitiesInTheActivityFeed")}
								/>
								<ValidationError
									{...formHandler
										.for("areNewEmailActivitiesEnabled")
										.attachToValidationError()
									}
								/>
							</FormItem>
							{!wasAlertDismissed && formData.areNewEmailActivitiesEnabled &&
								(
									<AlertBar
										kind="info"
										isDismissable
										isInline
										onDismiss={() => setWasAlertDismissed(true)}
									>
										{localize("_MakeSureThatOAuth2IsConfiguredAsSingleSignOnForThisApp")}
									</AlertBar>
								)
							}
						</>
					)
				}
			</Form>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
ExchangeSettings.displayName = "ExchangeSettings";

type DefaultActionsHandlers = {
	onSave(): void;
	onCancel(): void;
};

function listDefaultActions(
	handlers: DefaultActionsHandlers
): ToolbarAction<undefined>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.onSave(),
			icon: "floppy-disk",
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			canExecute: () => true,
			execute: () => handlers.onCancel(),
			icon: "xmark"
		}
	];
}

const convertToSaveModel = (initialData: EnvironmentExchangeSettingsViewModel): EnvironmentExchangeSettingsSavingModel => {
	return {
		areNewEmailActivitiesEnabled: initialData.areNewEmailActivitiesEnabled ?? false
	};
};

const DEFAULT_SAVE_MODEL: EnvironmentExchangeSettingsSavingModel = {
	areNewEmailActivitiesEnabled: false
};

export const environmentExchangeSettingsRouting = {
	page: "/EnvironmentExchangeSettings"
};